// The captcha half of the white-label public-onboarding gate — the widget
// side of `whitelabelOnboarding.js` (WS-SHELL-PANES-RENDER-TURNSTILE-WIDGET-1).
//
// ── What the server decides, and what we only obey ───────────────────────
// `GET /core/onboarding/options` returns `captcha: { provider, siteKey }`
// ONLY when the org requires one AND the environment resolves a provider.
// The site key is therefore READ FROM THAT RESPONSE and never written down
// here: a second org, a second environment, or a rotated key must need no
// redeploy. No site key in state → no widget, and the pane behaves exactly
// as it did before captcha existed.
//
// ── Why an EXPLICIT render, never the implicit one ───────────────────────
// Turnstile's implicit mode auto-scans the document for `.cf-turnstile` at
// script load. That scan races DOMQL insertion: the script and the element
// both arrive, the scan has already run, and NO iframe ever appears
// (measured on the landing rollout). So the script is loaded with
// `render=explicit` and `turnstile.render(node, …)` is called by hand once
// the API object exists — hence the poll in `ensureTurnstileApi`.
//
// ── Why a document-injected <script> ─────────────────────────────────────
// Turnstile ships one classic script on Cloudflare's own origin, which must
// stay on that origin to work. There is no importmap or ESM entry to route
// it through, so the injection lives HERE — one library function with `win`
// and `doc` injected — instead of in any component. Components stay
// declarative and every branch below is exercised without a browser.

export const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

// One in-flight load per window, so a pane that mounts the widget twice (or
// two panes on one page) share a single script and a single poll.
const _apiByWin = new WeakMap()

// An EXPLICIT `win: null` means "no browser here" and is honoured as such —
// `opts.win || window` would have fallen back to the global and, under a
// test runner that shares one process, quietly polled a shim window for ten
// seconds. Only an ABSENT key falls back.
const _winOf = (opts) =>
  opts.win === undefined ? (typeof window !== 'undefined' ? window : null) : opts.win

const _docOf = (opts, win) =>
  opts.doc === undefined
    ? (win && win.document) || (typeof document !== 'undefined' ? document : null)
    : opts.doc

const _apiOf = (win) => {
  const api = win && win.turnstile
  return api && typeof api.render === 'function' ? api : null
}

const _siteKeyOf = (s) => {
  const c = s && s.onboardingCaptcha
  const key = c && c.siteKey ? String(c.siteKey).trim() : ''
  return key || ''
}

/**
 * Resolve the Turnstile API object, loading its script once if needed.
 *
 * Returns the API or `null` — never throws and never rejects. A captcha the
 * visitor cannot load must leave the form usable (the server is the
 * authority on whether a token was required), so every failure path here is
 * a quiet `null`.
 */
export const ensureTurnstileApi = async function (opts = {}) {
  const win = _winOf(opts)
  const doc = _docOf(opts, win)
  if (!win || !doc) return null

  const ready = _apiOf(win)
  if (ready) return ready

  const pending = _apiByWin.get(win)
  if (pending) return pending

  const src = String(opts.src || TURNSTILE_SCRIPT_SRC)
  const timeoutMs = Number(opts.timeoutMs > 0 ? opts.timeoutMs : 10000)
  const stepMs = Number(opts.stepMs > 0 ? opts.stepMs : 50)
  const wait = opts.wait || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)))
  const now = opts.now || (() => Date.now())

  const load = (async () => {
    try {
      const already =
        typeof doc.querySelector === 'function' ? doc.querySelector(`script[src="${src}"]`) : null
      if (!already) {
        const target = doc.head || doc.body
        if (!target || typeof target.appendChild !== 'function') return null
        const tag = doc.createElement('script')
        tag.src = src
        tag.async = true
        tag.defer = true
        target.appendChild(tag)
      }

      const deadline = now() + timeoutMs
      // Poll rather than trust `onload`: the API object is published a tick
      // AFTER the script's load event on some builds, and an `already`
      // script may have loaded long before we asked.
      for (;;) {
        const api = _apiOf(win)
        if (api) return api
        if (now() >= deadline) return null
        await wait(stepMs)
      }
    } catch (_) {
      return null
    }
  })()

  _apiByWin.set(win, load)
  const api = await load
  // A failed load must not poison the next attempt (a remount, or a network
  // that came back), so only a SUCCESS stays cached.
  if (!api) _apiByWin.delete(win)
  return api
}

/**
 * Render the widget into this element's node and park its token in state.
 *
 * `this` is the DOMQL element (`el.call('mountWhitelabelTurnstile', …)`).
 * State contract — the pane owns these keys:
 *   reads  `onboardingCaptcha` `{ provider, siteKey }` (from the options call)
 *   writes `captchaToken`      the single-use token, or '' when it expires
 *   writes `captchaWidgetId`   the id the submit handler resets with
 * Every write is `preventUpdate` — a re-render of the pane would tear down
 * the iframe Cloudflare just mounted inside our node.
 */
export const mountWhitelabelTurnstile = async function (opts = {}) {
  const el = this
  const s = opts.state || (el && el.state) || null
  const node = opts.node || (el && el.node) || null
  if (!s || !node) return null

  // WAIT for the site key instead of bailing (bounded). The options call
  // lands AFTER first paint, and this shell runtime re-runs child FACTORIES
  // on the pane's state write without firing the child's own `onUpdate` —
  // measured live on app.bellforge.eu 2026-08-31: the slot div stood
  // display:block with the config parked in state, and no script was ever
  // injected because the only mount attempt ran pre-config and returned.
  // Polling the SHARED state object here needs no lifecycle at all. The
  // per-instance latch below still collapses concurrent pollers.
  let siteKey = _siteKeyOf(s)
  if (!siteKey) {
    const wait = opts.wait || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)))
    const now = opts.now || (() => Date.now())
    const keyTimeoutMs = Number(opts.keyTimeoutMs > 0 ? opts.keyTimeoutMs : 15000)
    const keyStepMs = Number(opts.keyStepMs > 0 ? opts.keyStepMs : 250)
    const deadline = now() + keyTimeoutMs
    for (;;) {
      await wait(keyStepMs)
      siteKey = _siteKeyOf(s)
      if (siteKey) break
      if (now() >= deadline) return null
    }
  }

  // Per-instance latch (set BEFORE the await): `onRender` and `onUpdate`
  // both call this, and the state write that reveals the widget lands while
  // the script is still loading.
  const scope = (el && el.scope) || null
  if (scope) {
    if (scope.turnstileSiteKey === siteKey) return scope.turnstileWidgetId || null
    scope.turnstileSiteKey = siteKey
  }

  const api = await ensureTurnstileApi(opts)
  if (!api) {
    if (scope) scope.turnstileSiteKey = null
    return null
  }

  const park = (token) =>
    s.update({ captchaToken: token ? String(token) : '' }, { preventUpdate: true })

  try {
    const widgetId = api.render(node, {
      sitekey: siteKey,
      // Managed mode usually issues the token INVISIBLY — no checkbox and no
      // visible challenge. This callback is the only proof it worked.
      callback: (token) => park(token),
      // A token dies after ~5 minutes and after one use. Clearing it here
      // keeps the pane honest: a stale token would 400 as `captcha_failed`.
      'expired-callback': () => park(''),
      'timeout-callback': () => park(''),
      'error-callback': () => park('')
    })
    if (scope) scope.turnstileWidgetId = widgetId
    s.update({ captchaWidgetId: widgetId || null }, { preventUpdate: true })
    return widgetId || null
  } catch (_) {
    if (scope) scope.turnstileSiteKey = null
    return null
  }
}

/**
 * Reset the widget and drop the spent token.
 *
 * Called from the SUBMIT handler, which is a different element from the
 * widget — so the id comes from state, not from `el.scope`. A Turnstile
 * token is single-use: without this, a second attempt replays a dead token
 * and the server answers `captcha_failed` for a visitor who did nothing
 * wrong.
 */
export const resetWhitelabelTurnstile = function (opts = {}) {
  const el = this
  const s = opts.state || (el && el.state) || null
  const win = _winOf(opts)
  const widgetId =
    opts.widgetId || (s && s.captchaWidgetId) || (el && el.scope && el.scope.turnstileWidgetId)

  if (s) s.update({ captchaToken: '' }, { preventUpdate: true })

  const api = win && win.turnstile
  if (!api || typeof api.reset !== 'function' || !widgetId) return false
  try {
    api.reset(widgetId)
    return true
  } catch (_) {
    return false
  }
}

/**
 * Tear the widget down with the element that owns it, so a remount renders
 * into a clean node instead of stacking a second iframe.
 */
export const removeWhitelabelTurnstile = function (opts = {}) {
  const el = this
  const s = opts.state || (el && el.state) || null
  const win = _winOf(opts)
  const scope = (el && el.scope) || null
  const widgetId = opts.widgetId || (scope && scope.turnstileWidgetId) || (s && s.captchaWidgetId)

  if (scope) {
    scope.turnstileWidgetId = null
    scope.turnstileSiteKey = null
  }

  const api = win && win.turnstile
  if (!api || typeof api.remove !== 'function' || !widgetId) return false
  try {
    api.remove(widgetId)
    return true
  } catch (_) {
    return false
  }
}
